import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  company: z.string().optional(),
  email: z.string().email("Enter a valid work email"),
  phone: z.string().optional(),
  topic: z.string().optional(),
  message: z.string().min(10, "Tell us a little about your project (min 10 characters)"),
  budget: z.string().optional(),
  company_url: z.string().optional(),
});

const TOPICS = ["AI & Machine Learning", "Data Engineering", "Analytics & BI", "Predictive Analytics", "Cloud Data", "Data Automation", "Not sure yet"];
const BUDGETS = ["< $10k", "$10k – $50k", "$50k – $100k", "$100k+", "Not sure yet"];

const fieldCls =
  "w-full rounded-xl border border-[#111827]/12 bg-white px-4 py-3 text-sm text-[#111827] placeholder:text-[#9CA3AF] outline-none transition-colors focus:border-[#1677FF]";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, data);
      toast.success("Thanks — we've received your message and will be in touch shortly.");
      setSubmitted(true);
      reset();
      setTimeout(() => setSubmitted(false), 6000);
    } catch (e) {
      toast.error("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <>
      <SEO
        title="Contact | All Data Tech"
        description="Tell us what you're trying to achieve. We'll help you identify the right data, AI or analytics approach. Book a consultation with All Data Tech."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title={<>Let's solve your <span className="text-gradient">data challenge.</span></>}
        sub="Tell us what you're trying to achieve. We'll help you identify the right data, AI or analytics approach."
      />

      <section className="ad-section bg-white pt-4">
        <div className="ad-container grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="space-y-6">
              <div className="ad-card p-7">
                <h2 className="text-lg font-bold text-[#111827]">Reach us directly</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex items-start gap-3 text-[#4B5563]">
                    <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1677FF]" />
                    <span>{COMPANY.address}</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#4B5563]">
                    <Mail className="h-5 w-5 flex-shrink-0 text-[#1677FF]" />
                    <a href={`mailto:${COMPANY.email}`} className="break-all hover:text-[#111827]">{COMPANY.email}</a>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-[#1677FF]/20 bg-[#1677FF]/5 p-7">
                <p className="text-sm font-bold text-[#111827]">Prefer a conversation first?</p>
                <p className="mt-2 text-sm text-[#4B5563]">Share a few details and we'll set up a consultation to understand your goals.</p>
              </div>
            </div>
          </Reveal>

          <Reveal index={1} className="lg:col-span-8">
            <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form" className="rounded-2xl border border-[#111827]/8 bg-[#F7F8FA] p-7 lg:p-9" noValidate>
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" {...register("company_url")} className="absolute left-[-9999px] h-0 w-0 opacity-0" />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name" error={errors.name}>
                  <input {...register("name")} data-testid="contact-name" className={fieldCls} placeholder="Jane Doe" />
                </Field>
                <Field label="Company">
                  <input {...register("company")} data-testid="contact-company" className={fieldCls} placeholder="Company name" />
                </Field>
                <Field label="Work Email" error={errors.email}>
                  <input {...register("email")} type="email" data-testid="contact-email" className={fieldCls} placeholder="you@company.com" />
                </Field>
                <Field label="Phone">
                  <input {...register("phone")} data-testid="contact-phone" className={fieldCls} placeholder="+1 555 000 0000" />
                </Field>
                <Field label="What can we help you with?">
                  <select {...register("topic")} data-testid="contact-topic" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select a topic</option>
                    {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Budget range (optional)">
                  <select {...register("budget")} data-testid="contact-budget" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select a range</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Project / Requirement" error={errors.message}>
                  <textarea {...register("message")} data-testid="contact-message" rows={5} className={fieldCls} placeholder="Tell us about your data, the problem you're solving and the outcome you want." />
                </Field>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1677FF] px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#0f5fe0] hover:shadow-[0_12px_30px_-10px_rgba(22,119,255,0.6)] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {isSubmitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : submitted ? (<><CheckCircle2 className="h-4 w-4" /> Message sent</>) : ("Start the Conversation")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[#374151]">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-xs text-red-500" data-testid="field-error">{error.message}</span>}
    </label>
  );
}
